import { AUDIO_SAMPLE_RATE } from './frames';
import { makeOpusHead } from './webm';

/**
 * WebCodecs plumbing shared by the export paths (Phase 4B): bounded queues, cancellable
 * waits and audio encoding. The constructors are injectable so the pipeline can be tested
 * with fakes.
 */

export interface CodecApis {
  VideoEncoder: typeof VideoEncoder;
  AudioEncoder: typeof AudioEncoder;
  VideoFrame: typeof VideoFrame;
  AudioData: typeof AudioData;
}

export const browserCodecApis = (): CodecApis => ({ VideoEncoder, AudioEncoder, VideoFrame, AudioData });

/** At most this many frames wait in an encoder queue (backpressure). */
export const MAX_ENCODE_QUEUE = 4;

/** Rejects with the signal's reason (an AbortError) once it is aborted. */
export function abortable(signal: AbortSignal): Promise<never> {
  return new Promise<never>((_, reject) => {
    const fail = () => reject(signal.reason instanceof Error ? signal.reason : new DOMException('Cancelled', 'AbortError'));
    if (signal.aborted) fail();
    else signal.addEventListener('abort', fail, { once: true });
  });
}

/** Lets the page breathe (input, paint, the Cancel click) without the 4 ms timer clamp. */
export function yieldToEventLoop(): Promise<void> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => {
      channel.port1.close();
      resolve();
    };
    channel.port2.postMessage(null);
  });
}

interface Queue {
  encodeQueueSize: number;
  addEventListener?(type: 'dequeue', fn: () => void): void;
  removeEventListener?(type: 'dequeue', fn: () => void): void;
}

/**
 * Waits until the encoder's queue is at most `limit` (backpressure), the encoder fails or
 * the export is cancelled. Every wait removes its own listeners (nothing accumulates over
 * thousands of frames); a 50 ms poll covers encoders that never emit 'dequeue'.
 */
export async function drain(encoder: Queue, limit: number, signal: AbortSignal, failed: () => Error | null): Promise<void> {
  while (encoder.encodeQueueSize > limit) {
    const error = failed();
    if (error) throw error;
    signal.throwIfAborted();
    await new Promise<void>((resolve) => {
      const done = () => {
        clearTimeout(timer);
        signal.removeEventListener('abort', done);
        encoder.removeEventListener?.('dequeue', done);
        resolve();
      };
      const timer = setTimeout(done, 50);
      signal.addEventListener('abort', done);
      encoder.addEventListener?.('dequeue', done);
    });
  }
  signal.throwIfAborted();
  const error = failed();
  if (error) throw error;
}

export interface EncodedAudio {
  packets: { data: Uint8Array; timestampUs: number }[];
  opusHead: Uint8Array;
  bytes: number;
}

/** Encodes a rendered AudioBuffer to Opus packets (1 s AudioData blocks with backpressure). */
export async function encodeOpus(
  buffer: Pick<AudioBuffer, 'numberOfChannels' | 'length' | 'sampleRate' | 'getChannelData'>,
  apis: CodecApis,
  config: AudioEncoderConfig,
  signal: AbortSignal,
  onProgress: (fraction: number) => void,
): Promise<EncodedAudio> {
  if (buffer.sampleRate !== AUDIO_SAMPLE_RATE) throw new Error(`Audio must be rendered at ${AUDIO_SAMPLE_RATE} Hz for Opus.`);
  const packets: EncodedAudio['packets'] = [];
  let head: Uint8Array | null = null;
  let bytes = 0;
  let failure: Error | null = null;
  const encoder = new apis.AudioEncoder({
    output: (chunk, meta) => {
      const data = new Uint8Array(chunk.byteLength);
      chunk.copyTo(data);
      packets.push({ data, timestampUs: chunk.timestamp });
      bytes += data.length;
      const d = meta?.decoderConfig?.description;
      if (d && !head) head = d instanceof ArrayBuffer ? new Uint8Array(d.slice(0)) : new Uint8Array((d as ArrayBufferView).buffer.slice((d as ArrayBufferView).byteOffset, (d as ArrayBufferView).byteOffset + (d as ArrayBufferView).byteLength));
    },
    error: (e) => (failure ??= new Error(`Audio encoding failed: ${e.message}`)),
  });
  try {
    encoder.configure(config);
    const channels = Math.min(2, buffer.numberOfChannels);
    const block = buffer.sampleRate; // 1 s
    for (let from = 0; from < buffer.length; from += block) {
      await drain(encoder, MAX_ENCODE_QUEUE, signal, () => failure);
      const n = Math.min(block, buffer.length - from);
      const planar = new Float32Array(n * 2);
      for (let c = 0; c < 2; c++) planar.set(buffer.getChannelData(Math.min(c, channels - 1)).subarray(from, from + n), c * n);
      const data = new apis.AudioData({ format: 'f32-planar', sampleRate: buffer.sampleRate, numberOfFrames: n, numberOfChannels: 2, timestamp: Math.round((from * 1e6) / buffer.sampleRate), data: planar });
      encoder.encode(data);
      data.close();
      onProgress((from + n) / buffer.length);
    }
    await Promise.race([encoder.flush(), abortable(signal)]);
    if (failure) throw failure;
    // Chrome supplies an OpusHead (with its encoder delay); otherwise assume none.
    return { packets, opusHead: head ?? makeOpusHead(2, AUDIO_SAMPLE_RATE, 0), bytes };
  } finally {
    if (encoder.state !== 'closed') encoder.close();
  }
}
